import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormTextArea } from '@/components/forms/form-text-area';
import { InfoKeyValue } from '@/components/info-key-value';
import { useToast } from '@/components/ui/hooks/use-toast';
import { LoaderIcon } from '@/components/ui/loader-icon';
import {
  createStudentGroupReassignmentRequest,
  getCoachClasses,
  getPendingStudentGroupReassignmentRequests,
  getStudentGroupReassignmentRequests,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { cn } from '@/lib/tailwind-utils';
import { formatPlainDate } from '@airreading/common';
import {
  adjustMinimalClassTimeAcrossTZ,
  CoachViewableStudentGroupWithScheduleAndLevel,
  formatReassignmentReason,
  StudentGroupReassignmentReason,
  StudentGroupReassignmentRequestStatus,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting';

/** Earliest selectable remove date: two calendar days from today (skip today and tomorrow). */
function earliestRemoveEffectiveDate() {
  return Temporal.Now.plainDateISO().add({ days: 2 });
}

const reasonMaxLength = 150;

const formSchema = z.object({
  effectiveDate: z
    .instanceof(Temporal.PlainDate, { message: 'Remove effective date is required' })
    .refine((date) => Temporal.PlainDate.compare(date, earliestRemoveEffectiveDate()) >= 0, {
      message: 'Remove effective date must be at least 2 days from today',
    }),
  reason: z.string().max(reasonMaxLength, `Reason must be ${reasonMaxLength} characters or fewer.`).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface RemoveStudentGroupModalProps {
  classData: CoachViewableStudentGroupWithScheduleAndLevel;
  requesterId: string;
  teacherTimeZone?: string | null;
  trigger: ReactNode;
}

export const RemoveStudentGroupModal = ({
  classData,
  requesterId,
  teacherTimeZone,
  trigger,
}: RemoveStudentGroupModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const isPendingRemoval = classData.hasPendingReassignmentRequest;
  const {
    data: reassignmentRequestData,
    isLoading: isPendingRequestLoading,
    error: pendingRequestError,
  } = useQuery({
    ...getStudentGroupReassignmentRequests(classData.id),
    enabled: isPendingRemoval && isOpen,
  });
  const pendingRemovalRequest = reassignmentRequestData?.reassignmentRequests.find(
    ({ displayStatus }) =>
      displayStatus === StudentGroupReassignmentRequestStatus.requested ||
      displayStatus === StudentGroupReassignmentRequestStatus.processing,
  );
  const { schedule } = classData.classTime;
  const displayTimeZone = teacherTimeZone ?? schedule.timeZone;
  const students = classData.students.map((student) => student.coachFacingDisplayName?.trim() || '-').join(', ');
  const classTimes = formatStudentClassTimeAbbr(
    adjustMinimalClassTimeAcrossTZ(classData.classTime, schedule.school.org.timeZone, displayTimeZone),
  );

  const { mutateAsync } = useMutation({
    mutationFn: createStudentGroupReassignmentRequest,
  });

  const minEffectiveDate = earliestRemoveEffectiveDate();
  const defaultValues: DefaultValues<FormValues> = {
    effectiveDate: minEffectiveDate,
    reason: '',
  };

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<FormValues>) => {
    if (isPendingRemoval) {
      closeModal();
      return;
    }

    const teacherReason = formData.reason?.trim() || null;

    try {
      await mutateAsync({
        studentGroupId: classData.id,
        effectivePlainDate: formData.effectiveDate,
        requesterId,
        reasons: teacherReason ? [] : [StudentGroupReassignmentReason.teacher_request],
        preferredCoachId: null,
        specialRequirements: null,
        countAsReassignedFromTestTeacher: false,
        teacherReason,
      });

      toast({
        title: 'Success',
        description: 'Removal request submitted successfully',
      });

      queryClient.invalidateQueries(getCoachClasses(requesterId, true));
      queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
      queryClient.invalidateQueries(getStudentGroupReassignmentRequests(classData.id));

      reset();
      closeModal();
    } catch (error) {
      const airServerError = airServerErrorFrom(error);
      toast({
        title: 'Error',
        description: airServerError?.message ?? 'Failed to submit removal request',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle={isPendingRemoval ? 'Removal request' : 'Remove student group'}
      formSchema={formSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onOpenChange={setIsOpen}
      trigger={trigger}
      confirmBtnLabel="Confirm"
      hideConfirmButton={isPendingRemoval}
      cancelBtnLabel={isPendingRemoval ? 'Close' : 'Cancel'}
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg max-h-none gap-3 p-4' }}
      dataTestId="remove-student-group-modal"
    >
      {({ form }) => {
        const reasonLength = form.watch('reason')?.length ?? 0;
        const isOverReasonLimit = reasonLength > reasonMaxLength;

        return (
          <div className="space-y-5 py-4">
            <p className="text-sm text-destructive">
              {isPendingRemoval
                ? 'This student group is pending removal. The request will be processed within 24 hours.'
                : 'Please confirm your request to remove this student group. Once confirmed, the removal request will be processed within 24 hours.'}
            </p>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <InfoKeyValue label="District" value={schedule.school.org.name} />
              <InfoKeyValue label="Students" value={students} />
              <InfoKeyValue
                label={
                  <>
                    Class times
                    <span className="block pt-1 font-normal text-muted-foreground">{displayTimeZone}</span>
                  </>
                }
                value={classTimes}
              />
              <InfoKeyValue label="Last class date" value={formatPlainDate(schedule.endPlainDate)} />
            </div>

            {isPendingRemoval ? (
              isPendingRequestLoading ? (
                <LoaderIcon className="h-5 w-5" />
              ) : pendingRequestError || !pendingRemovalRequest ? (
                <p className="text-sm text-destructive">Failed to load the pending removal request.</p>
              ) : (
                <>
                  <InfoKeyValue
                    label="Remove effective date"
                    value={formatPlainDate(pendingRemovalRequest.effectivePlainDate)}
                  />
                  <InfoKeyValue label="Reason" value={formatReassignmentReason(pendingRemovalRequest)} />
                </>
              )
            ) : (
              <>
                <FormInputDate
                  form={form}
                  name="effectiveDate"
                  label="Remove effective date"
                  required
                  minDate={minEffectiveDate}
                  triggerButtonProps={{ className: 'h-10 w-full' }}
                />

                <FormTextArea
                  form={form}
                  name="reason"
                  label="Reason"
                  labelEndContent={
                    <span className={cn('text-sm text-muted-foreground', isOverReasonLimit && 'text-destructive')}>
                      {reasonLength} / {reasonMaxLength}
                    </span>
                  }
                  textAreaProps={{
                    placeholder: 'Please leave your removal reason here (optional)',
                    className: 'min-h-[96px]',
                    maxLength: reasonMaxLength,
                  }}
                />
              </>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
